import { Link } from "react-router-dom";
import { ArrowRightIcon, LockIcon, CheckIcon, FlaskConicalIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImportanceBadge } from "@/components/book/importance-badge";
import { ReadingMap } from "@/components/book/reading-map";
import { ChapterVideoSummary } from "@/components/book/chapter-video-summary";
import { t } from "@/lib/typography";
import type { ChapterDetail } from "@/types/book";

function AssessmentCTA({ chapter }: { chapter: ChapterDetail }) {
  if (chapter.is_completed && chapter.assessment_id != null) {
    return (
      <div className="flex items-center justify-center gap-3">
        <span className="flex items-center gap-1.5 font-medium text-green-600 dark:text-green-400">
          <CheckIcon className="size-4" />
          Assessment completed
        </span>
        <Link to={`/assessments/${chapter.assessment_id}/take`}>
          <Button variant="outline" size="lg" className="gap-1.5">
            Review
            <ArrowRightIcon className="size-4" />
          </Button>
        </Link>
      </div>
    );
  }

  if (chapter.assessment_id != null) {
    return (
      <div className="flex justify-center">
        <Link to={`/assessments/${chapter.assessment_id}/take`}>
          <Button size="lg" className="gap-1.5">
            Take Assessment
            <ArrowRightIcon className="size-4" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <p className="flex items-center justify-center gap-2 text-muted-foreground">
      <LockIcon className="size-4" />
      {chapter.order <= 1
        ? "Complete the starting assessment first"
        : `Complete Chapter ${chapter.order - 1} first`}
    </p>
  );
}

// Optional hands-on lab entry point. Access is enforced by the lab page itself,
// so we only gate the CTA's presence on the chapter carrying a lab link.
function PracticeLabCTA({ chapter }: { chapter: ChapterDetail }) {
  if (chapter.practice_lab_id == null) return null;

  return (
    <div className="flex justify-center">
      <Link to={`/practice-labs/${chapter.practice_lab_id}`}>
        <Button variant="outline" size="lg" className="gap-1.5">
          <FlaskConicalIcon className="size-4" />
          Open Practice Lab
          <ArrowRightIcon className="size-4" />
        </Button>
      </Link>
    </div>
  );
}

export function ChapterContent({ chapter }: { chapter: ChapterDetail }) {
  return (
    <div className="space-y-8">
      {/* Title + metadata */}
      <div className="space-y-3">
        <h1 className={t.pageTitle}>{chapter.title}</h1>
        <div className="flex flex-wrap items-center gap-4">
          <ImportanceBadge importance={chapter.importance} />
          {chapter.time_estimate && <span>{chapter.time_estimate}</span>}
          {chapter.page_count > 0 && <span>~{chapter.page_count} pages</span>}
        </div>
      </div>

      <div className="border-t" />

      {/* Why this rating — opening paragraph, no heading */}
      {chapter.reading_map?.why_rating && (
        <p className="leading-relaxed">{chapter.reading_map.why_rating}</p>
      )}

      {/* Reading map sections */}
      {chapter.reading_map && <ReadingMap readingMap={chapter.reading_map} />}

      {/* Video summary */}
      {chapter.video_summary && (
        <ChapterVideoSummary src={chapter.video_summary} title={chapter.title} />
      )}

      <div className="border-t" />

      {/* Assessment CTA */}
      <AssessmentCTA chapter={chapter} />

      {/* Optional hands-on practice lab */}
      <PracticeLabCTA chapter={chapter} />
    </div>
  );
}
